import { nextStepFor } from "./constants";
import { VEHICLE_PHOTO_MODES } from "../../lib/conciergeFeature";

export const JOB_ACTION = {
  ACCEPT: "ACCEPT",
  STEP: "STEP",
  CONFIRM_PARKED: "CONFIRM_PARKED",
  COLLECTION_PHOTOS: "COLLECTION_PHOTOS",
  HAND_OVER: "HAND_OVER",
  LODGE_PICKUP: "LODGE_PICKUP",
  START_INTAKE: "START_INTAKE",
  MATCH: "MATCH",
  CHANGE_PICKUP: "CHANGE_PICKUP",
  CANCEL_PICKUP: "CANCEL_PICKUP",
};

const RESCHEDULABLE_STATUSES = ["REQUESTED", "RUNNER_ASSIGNED"];

export const AUDIENCE = { RUNNER: "runner", DESK: "desk" };

const HANDOVER_BLOCKED =
  "Payment outstanding. A manager must resolve this first.";

const carryingBlocked = (plate) =>
  `You already have ${plate ?? "another car"} in hand. Finish that one first.`;

const unpaid = (row) => row.paymentRequired !== false && row.paid !== true;

/*
 * What the desk can do to this car from the booking view, at the stage it is
 * at. A car in a runner's hands has no desk action at all: the taps between
 * accepting a pickup and reaching the door are the runner's, and offering them
 * here would only produce a refusal.
 *
 * Both slots are returned together because a queued pickup is two answers
 * at once - move it (to now, or another time), or call it off.
 */
export function deskActionsFor({ row, canHandOver = false }) {
  if (!row) return { primary: null, secondary: null };

  /* No session yet: an expected arrival the desk is about to take in. */
  if (!row.status) {
    return {
      primary:
        row.bookingStatus === "PENDING_ENTRY"
          ? { key: JOB_ACTION.START_INTAKE, label: "Mark arrived & start intake" }
          : null,
      secondary: null,
    };
  }

  if (row.status === "DETECTED") {
    return {
      primary: row.bookingId
        ? { key: JOB_ACTION.START_INTAKE, label: "Claim & start intake" }
        : { key: JOB_ACTION.MATCH, label: "Match booking" },
      secondary: null,
    };
  }

  if (row.status === "PARKED") {
    if (!row.bookingId) return { primary: null, secondary: null };

    return {
      primary: { key: JOB_ACTION.LODGE_PICKUP, label: "Lodge pickup" },
      secondary: null,
    };
  }

  if (RESCHEDULABLE_STATUSES.includes(row.status) && row.bookingId) {
    return {
      primary: { key: JOB_ACTION.CHANGE_PICKUP, label: "Change pickup time" },
      secondary: { key: JOB_ACTION.CANCEL_PICKUP, label: "Cancel pickup" },
    };
  }

  if (row.status === "READY" && canHandOver) {
    return {
      primary: {
        key: JOB_ACTION.HAND_OVER,
        label: "Handed to guest",
        disabled: unpaid(row),
        reason: unpaid(row) ? HANDOVER_BLOCKED : null,
      },
      secondary: null,
    };
  }

  return { primary: null, secondary: null };
}

/*
 * The one thing to do to this car next, for whoever is looking at it. The queue
 * row and the booking view both read it, so a row and the modal it opens can
 * never disagree about what comes next.
 *
 * The desk's Lodge pickup is offered here and disabled by the caller: whether a
 * balance blocks it needs the live charge, which is a request per booking and
 * belongs to the screen that opened one.
 */
export function primaryActionFor({
  row,
  audience = AUDIENCE.RUNNER,
  carrying = null,
  vehiclePhotoMode = VEHICLE_PHOTO_MODES.OPTIONAL,
}) {
  if (!row) return null;

  if (audience === AUDIENCE.DESK) return deskActionsFor({ row }).primary;

  const unheld = !row.runnerId;

  if (unheld) {
    /* Walking an arrival to a bay is a car in the runner's hands from the tap;
       taking a pickup ticket is not, so only the park waits on the rest. */
    if (row.status === "CLAIMED") {
      return {
        key: JOB_ACTION.ACCEPT,
        label: "Accept job",
        disabled: Boolean(carrying),
        reason: carrying ? carryingBlocked(carrying.plate) : null,
      };
    }

    return row.status === "REQUESTED"
      ? { key: JOB_ACTION.ACCEPT, label: "Accept job" }
      : null;
  }

  if (!row.mine) return null;

  if (row.status === "PARKING") {
    return { key: JOB_ACTION.CONFIRM_PARKED, label: "Confirm parked" };
  }

  if (row.status === "READY") {
    return {
      key: JOB_ACTION.HAND_OVER,
      label: "Handed to guest",
      /* The runner is stopped here and told who can clear it - taking the
         payment is desk work. */
      disabled: row.unpaid === true,
      reason: row.unpaid === true ? HANDOVER_BLOCKED : null,
    };
  }

  const step = nextStepFor(row.status);

  if (!step) return null;

  if (row.status === "RUNNER_ASSIGNED" && carrying) {
    return {
      key: JOB_ACTION.STEP,
      label: step.action,
      step,
      disabled: true,
      reason: carryingBlocked(carrying.plate),
    };
  }

  /* The car is collected and nobody is waiting on it yet, so the condition set
     is offered here rather than at the door. The facility setting decides
     whether this step is bypassed, required, or shown with a Skip action. */
  if (row.status === "VEHICLE_LOCATED") {
    if (vehiclePhotoMode === VEHICLE_PHOTO_MODES.DISABLED) {
      return { key: JOB_ACTION.STEP, label: step.action, step };
    }

    return {
      key: JOB_ACTION.COLLECTION_PHOTOS,
      label: "Take photos",
      step,
      ...(vehiclePhotoMode === VEHICLE_PHOTO_MODES.OPTIONAL && {
        secondary: { key: JOB_ACTION.STEP, label: "Skip", step },
      }),
    };
  }

  return { key: JOB_ACTION.STEP, label: step.action, step };
}
